import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronRight, MapPin, Check, AlertTriangle, Wrench, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';

const RemplacementPompeNantesPage = () => {
  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Accueil', url: '/' },
    { name: 'Pièces détachées', url: '/pieces-detachees' },
    { name: 'Remplacement pompe spa Nantes', url: '/remplacement-pompe-spa-nantes' }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Remplacement de pompe de spa à Nantes",
    "description": "Service de remplacement de pompe de spa par un technicien spécialiste à Nantes et Grand Ouest (44, 85, 56, 49). Diagnostic, fourniture et installation.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Atlantique Spa Boutique",
      
      "areaServed": ["Nantes", "Loire-Atlantique", "Vendée", "Morbihan", "Maine-et-Loire"]
    },
    "serviceType": "Remplacement de pompe",
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 47.2184,
        "longitude": -1.5536
      },
      "geoRadius": "100"
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="remplacement-pompe-page">
      <SEO
        title="Remplacement de pompe de spa à Nantes | Technicien spécialiste 44, 85, 56, 49"
        description="Service de remplacement de pompe de spa à Nantes et Grand Ouest. Diagnostic, fourniture et installation par un technicien spécialiste. Intervention Loire-Atlantique, Vendée, Morbihan, Maine-et-Loire."
        canonical="/remplacement-pompe-spa-nantes"
        customSchema={serviceSchema}
        breadcrumbs={breadcrumbs}
      />

      {/* Hero Section - Full Width Image */}
      <FullWidthHero
        variant="page"
        image={realPhotos.expertise}
        imageAlt={realPhotosAlt.expertise}
        badge="Intervention Grand Ouest (44, 85, 56, 49)"
        badgeIcon={MapPin}
        title="Remplacement de pompe de spa à Nantes"
        subtitle="Votre pompe de spa montre des signes de faiblesse ? Notre technicien spécialiste intervient à Nantes et dans tout le Grand Ouest pour le diagnostic et le remplacement de vos pompes."
        primaryCta={{ text: "Nous contacter", href: "mailto:contact@atlantiquespaboutique.com", icon: Mail }}
        overlayPosition="left"
      />

      {/* When to replace section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quand faut-il remplacer une pompe de spa ?
            </h2>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto">
              Reconnaître les signes d'usure pour intervenir avant une panne complète
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: '🔊',
                title: 'Bruit anormal',
                description: 'Grondement, grincement ou vibrations excessives indiquant une usure des roulements.'
              },
              {
                icon: '💧',
                title: 'Fuite d\'eau',
                description: 'Fuite au niveau du joint d\'étanchéité ou du corps de pompe.'
              },
              {
                icon: '⚡',
                title: 'Problème électrique',
                description: 'La pompe ne démarre plus, disjoncte ou chauffe anormalement.'
              },
              {
                icon: '💨',
                title: 'Jets faibles',
                description: 'Pression des jets insuffisante malgré un filtre propre.'
              }
            ].map((item, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
                <span className="text-3xl mb-4 block">{item.icon}</span>
                <h3 className="font-semibold text-[#0F172A] mb-2">{item.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-6 flex gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">Ne tardez pas à intervenir</h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                Une pompe défaillante peut endommager d'autres composants du spa (carte électronique, chauffage) et entraîner des coûts de réparation plus importants.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mono vs Bi-vitesse */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-10 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pompe mono-vitesse ou bi-vitesse ?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Mono-vitesse */}
            <div className="bg-white rounded-xl p-8 border border-[#E2E8F0]">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-10 h-10 bg-[#1E3A5F]/10 rounded-lg flex items-center justify-center text-[#1E3A5F]">1</span>
                Pompe mono-vitesse
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-[#475569]">
                  <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Fonctionne à une seule vitesse (puissance maximale)
                </li>
                <li className="flex items-start gap-2 text-[#475569]">
                  <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Construction simple et robuste
                </li>
                <li className="flex items-start gap-2 text-[#475569]">
                  <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Prix d'achat plus accessible
                </li>
              </ul>
              <p className="text-sm text-[#64748B] bg-[#F8FAFC] p-3 rounded-lg">
                <strong>Idéale pour :</strong> Pompes de massage, utilisation ponctuelle
              </p>
            </div>

            {/* Bi-vitesse */}
            <div className="bg-white rounded-xl p-8 border border-[#1E3A5F]">
              <h3 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <span className="w-10 h-10 bg-[#1E3A5F] rounded-lg flex items-center justify-center text-white">2</span>
                Pompe bi-vitesse
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-[#475569]">
                  <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Deux régimes : basse et haute vitesse
                </li>
                <li className="flex items-start gap-2 text-[#475569]">
                  <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Économie d'énergie en mode filtration
                </li>
                <li className="flex items-start gap-2 text-[#475569]">
                  <Check className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  Fonctionnement silencieux en basse vitesse
                </li>
              </ul>
              <p className="text-sm text-[#64748B] bg-[#1E3A5F]/5 p-3 rounded-lg">
                <strong>Idéale pour :</strong> Pompes de circulation, filtration continue 24h/24
              </p>
            </div>
          </div>

          <p className="text-center text-[#475569] mt-8 text-sm">
            Pas sûr du type de pompe qu'il vous faut ? <a href="mailto:contact@atlantiquespaboutique.com" className="text-[#1E3A5F] font-semibold hover:underline">Contactez-nous</a> pour un conseil personnalisé.
          </p>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Zone d'intervention
            </h2>
            <p className="text-lg text-[#475569]">
              Notre technicien se déplace à votre domicile
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { dept: '44', name: 'Loire-Atlantique', cities: 'Nantes, Saint-Nazaire, Saint-Herblain, Rezé...' },
              { dept: '85', name: 'Vendée', cities: 'La Roche-sur-Yon, Les Sables-d\'Olonne, Challans...' },
              { dept: '56', name: 'Morbihan', cities: 'Vannes, Lorient, Auray, Pontivy...' },
              { dept: '49', name: 'Maine-et-Loire', cities: 'Angers, Cholet, Saumur...' }
            ].map((zone) => (
              <div key={zone.dept} className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0] text-center">
                <div className="w-12 h-12 bg-[#1E3A5F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">{zone.dept}</span>
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">{zone.name}</h3>
                <p className="text-sm text-[#64748B]">{zone.cities}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Nos pompes de spa
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <Link 
              to="/pieces-detachees/pompes-circulation" 
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all flex items-center gap-4 group"
            >
              <div className="w-14 h-14 bg-[#1E3A5F]/10 rounded-xl flex items-center justify-center">
                <Wrench className="w-7 h-7 text-[#1E3A5F]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#0F172A] group-hover:text-[#1E3A5F] mb-1">Pompes de circulation</h3>
                <p className="text-sm text-[#64748B]">LX Whirlpool, Laing, Waterway...</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#1E3A5F]" />
            </Link>

            <Link 
              to="/pieces-detachees/pompes-massage" 
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all flex items-center gap-4 group"
            >
              <div className="w-14 h-14 bg-[#F97316]/10 rounded-xl flex items-center justify-center">
                <Wrench className="w-7 h-7 text-[#F97316]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#0F172A] group-hover:text-[#1E3A5F] mb-1">Pompes de massage</h3>
                <p className="text-sm text-[#64748B]">Mono et bi-vitesse, 1.5HP à 4HP</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#1E3A5F]" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1E3A5F] to-[#2A4A70] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Besoin d'un remplacement de pompe ?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Contactez-nous pour un diagnostic et un devis gratuit
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg h-14 px-8 font-semibold">
                <Mail className="w-5 h-5 mr-2" />
                Nous contacter
              </Button>
            </a>
          </div>
          <p className="text-sm text-white/60 mt-6 flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            Réponse sous 24h • Intervention rapide
          </p>
        </div>
      </section>
    </div>
  );
};

export default RemplacementPompeNantesPage;
